import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { InstagramEmbed } from "@/components/InstagramEmbed";
import { articleAnchorId } from "@/data/search-index";
import { recordContentView } from "@/lib/content-views";
import { AutoVideo } from "@/components/AutoVideo";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MediaSection, Reveal, useReveal } from "@/components/portfolio-scroll";

/** <li> avec fondu à l'entrée dans le viewport (désactivé si prefers-reduced-motion). */
function RevealLi({ id, className = "", children }: { id?: string; className?: string; children: ReactNode }) {
  const { ref, className: revealClass } = useReveal<HTMLLIElement>();
  return (
    <li ref={ref} id={id} className={`${className} ${revealClass}`}>
      {children}
    </li>
  );
}

export const Route = createFileRoute("/portfolio")({
  head: ({ loaderData }) => {
    // Le typage du loader n'est pas encore résolu ici (référence circulaire) : on le décrit.
    const data = loaderData as unknown as { allArticles: OutletArticle[]; outlets: MediaOutlet[] } | undefined;
    return {
      meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
        { title: "Portfolio — Réalisations de Mathys Gautier" },
        {
          name: "description",
          content:
            "Des podcasts au datajournalisme : découvrez les réalisations de Mathys Gautier au Parisien, à Citoyens.com, à Sciences Po TV et à Radio Germaine.",
        },
        { name: "robots", content: "noimageindex" },
        { property: "og:title", content: "Portfolio — Réalisations de Mathys Gautier" },
        {
          property: "og:description",
          content: "Des podcasts au datajournalisme : découvrez les réalisations de Mathys Gautier au Parisien, à Citoyens.com, à Sciences Po TV et à Radio Germaine.",
        },
        { property: "og:url", content: "https://mathysgautier.com/portfolio" },
      ],
      links: [{ rel: "canonical", href: "https://mathysgautier.com/portfolio" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Portfolio — Réalisations de Mathys Gautier",
            url: "/portfolio",
            author: { "@type": "Person", name: "Mathys Gautier" },
            about: "Datajournalisme, PQR, vidéo et podcast",
            mainEntity: {
              "@type": "ItemList",
              itemListElement: (data?.allArticles ?? []).map((a, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "NewsArticle",
                  headline: a.title,
                  name: a.title,
                  url: a.href,
                  datePublished: a.date,
                  description: a.excerpt,
                  image: a.imageUrl ? [a.imageUrl] : undefined,
                  author: { "@type": "Person", name: "Mathys Gautier" },
                  publisher: {
                    "@type": "Organization",
                    name: (data?.outlets ?? []).find((o) => o.slug === a.outlet)?.title ?? a.outlet,
                  },
                  isAccessibleForFree: a.access === "Gratuit",
                },
              })),
            },
          }),
        },
      ],
    };
  },

  loader: ({ context }) => context.queryClient.ensureQueryData(portfolioQueryOptions),

  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-6xl px-6 py-24 text-sm">
      {error instanceof Error ? error.message : "Une erreur est survenue."}
    </div>
  ),
  notFoundComponent: () => <div className="mx-auto max-w-6xl px-6 py-24 text-sm">Contenu introuvable.</div>,
  component: Portfolio,
});

type Access = "Premium" | "Gratuit";

interface Article {
  id?: string;
  title: string;
  excerpt: string;
  href: string;
  date: string; // ISO YYYY-MM-DD
  dateLabel: string;
  access: Access;
  favorite?: boolean;
  coauthor?: string;
  section?: "Articles" | "Dossiers";
  imageUrl?: string;
  imageCredit?: string;
  imageAlt?: string;
  mediaType?: "image" | "video";
}

interface MediaOutlet {
  slug: string;
  eyebrow: string;
  title: string;
  place: string | null;
  period: string;

  content_type: string[];
}

type OutletArticle = Article & { outlet: string };
type OutletChronique = ChroniqueItem & { outlet: string; date: string };
type OutletEpisode = Episode & { outlet: string; date: string };

const OUTLET_FOOTER: Record<string, { prefix: string; href: string; label: string }> = {
  parisien: {
    prefix: "Retrouver toutes les publications sur",
    href: "https://www.leparisien.fr/auteur/mathys-gautier/",
    label: "leparisien.fr/auteur/mathys-gautier",
  },
  citoyens: {
    prefix: "Retrouver l’ensemble des publications sur",
    href: "https://citoyens.com/",
    label: "citoyens.com",
  },
  "sciences-po-tv": {
    prefix: "Retrouver toutes les vidéos sur",
    href: "https://www.instagram.com/sciencespotv/",
    label: "instagram.com/sciencespotv",
  },
  "radio-germaine": {
    prefix: "Retrouver tous les épisodes sur",
    href: "https://podcasts.apple.com/my/podcast/radio-germaine/id1439013432",
    label: "Apple Podcasts — Radio Germaine",
  },
};

interface ChroniqueItem {
  id: string;
  title: string;
  excerpt: string;
  reelUrl: string;
}

interface Episode {
  id: string;
  n: string;
  title: string;
  excerpt: string;
  apple: string;
  spotify: string;
  duration: string;
  favorite?: boolean;
}

const MONTHS_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

function formatDateLabel(iso: string): string {
  // iso is "YYYY-MM-DD"
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS_FR[m - 1]} ${y}`;
}

function ArticleCard({ a, isNew }: { a: Article; isNew?: boolean }) {
  return (
    <a
      href={a.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => recordContentView("article", a.id)}
      onAuxClick={(e) => {
        if (e.button === 1) recordContentView("article", a.id);
      }}
      className="liquid-glass group flex h-full flex-col justify-between rounded-2xl p-6 micro-card hover:bg-secondary"
    >
      <div>
        <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
          <span className="flex items-center gap-2">
            {a.dateLabel}
            {isNew && (
              <span className="rounded-full bg-ocean px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.2em] text-paper">
                Nouveau
              </span>
            )}
          </span>
          <span className="flex items-center gap-2">
            {a.favorite && (
              <span
                title="Coup de cœur"
                aria-label="Coup de cœur"
                className="text-base leading-none text-red-600 transition-transform duration-300 group-hover:scale-110"
              >
                ♥
              </span>
            )}
            <span className="rounded-full border border-border px-2 py-0.5">{a.access}</span>
          </span>
        </div>
        <h3 className="mt-5 font-serif text-xl leading-snug decoration-muted-foreground underline-offset-4 group-hover:underline">
          {a.title}
        </h3>
        {a.imageUrl && (
          <figure className="mt-4">
            <div className="img-zoom-container overflow-hidden rounded-md border border-border bg-muted">
              {a.mediaType === "video" ? (
                <AutoVideo src={a.imageUrl} className="img-zoom block h-auto w-full" ariaHidden />
              ) : (
                <img src={a.imageUrl} alt={a.imageAlt ?? ""} loading="lazy" className="img-zoom block h-auto w-full" />
              )}
            </div>
            {a.imageCredit && (
              <figcaption className="mt-2 text-left text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground">
                Crédits : {a.imageCredit}
              </figcaption>
            )}
          </figure>
        )}
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>

        {a.coauthor && <p className="mt-3 text-xs italic text-muted-foreground">Écrit avec {a.coauthor}</p>}
      </div>
      <p className="mt-6 text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground decoration-muted-foreground underline-offset-4 transition-colors group-hover:text-ocean group-hover:underline">
        Lire l’article
      </p>
    </a>
  );
}

function sortDesc(list: Article[]) {
  const byDateDesc = (a: Article, b: Article) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);
  const favs = list.filter((a) => a.favorite).sort(byDateDesc);
  const rest = list.filter((a) => !a.favorite).sort(byDateDesc);
  return [...favs, ...rest];
}

type Filter = "all" | "fav" | "gratuit" | "premium";

const portfolioQueryOptions = queryOptions({
  queryKey: ["portfolio"],
  queryFn: async () => {
    const [articlesRes, igRes, podcastRes, outletsRes] = await Promise.all([
      supabase
        .from("articles")
        .select(
          "id,title,excerpt,href,date,outlet,section,access,favorite,coauthor,image_url,image_credit,image_alt,media_type",
        )
        .eq("is_published", true),
      supabase
        .from("instagram_posts")
        .select("id,title,excerpt,reel_url,outlet,date,display_order,created_at")
        .eq("is_published", true)
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true }),
      supabase
        .from("podcast_episodes")
        .select(
          "id,episode_label,title,excerpt,apple_url,spotify_url,duration,favorite,outlet,date,display_order,created_at",
        )
        .eq("is_published", true)
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true }),
      supabase.from("media_outlets").select("slug,eyebrow,title,place,period,content_type").eq("is_published", true),
    ]);
    if (articlesRes.error) throw articlesRes.error;
    if (igRes.error) throw igRes.error;
    if (podcastRes.error) throw podcastRes.error;
    if (outletsRes.error) throw outletsRes.error;

    const rows = articlesRes.data ?? [];
    const mapArticle = (r: (typeof rows)[number]): Article => ({
      id: r.id,
      title: r.title,
      excerpt: r.excerpt ?? "",
      href: r.href ?? "",
      date: r.date ?? "",
      dateLabel: formatDateLabel(r.date ?? ""),
      access: (r.access as Access | null) ?? "Gratuit",
      favorite: r.favorite ?? undefined,
      coauthor: r.coauthor ?? undefined,
      section: (r.section as "Articles" | "Dossiers" | null) ?? undefined,
      imageUrl: r.image_url ?? undefined,
      imageCredit: r.image_credit ?? undefined,
      imageAlt: r.image_alt ?? undefined,
      mediaType: (r.media_type as "image" | "video" | null) ?? "image",
    });

    return {
      outlets: (outletsRes.data ?? []) as MediaOutlet[],
      parisien: rows.filter((r) => r.outlet === "parisien").map(mapArticle),
      citoyens: rows.filter((r) => r.outlet === "citoyens").map(mapArticle),
      allArticles: rows.map((r) => ({ ...mapArticle(r), outlet: r.outlet })) as OutletArticle[],
      allChroniques: (igRes.data ?? []).map((r) => ({
        id: r.id,
        title: r.title,
        excerpt: r.excerpt ?? "",
        reelUrl: r.reel_url ?? "",
        outlet: r.outlet ?? "",
        date: r.date ?? "",
      })) as OutletChronique[],
      allEpisodes: (podcastRes.data ?? []).map((r) => ({
        id: r.id,
        n: r.episode_label,
        title: r.title,
        excerpt: r.excerpt ?? "",
        apple: r.apple_url ?? "",
        spotify: r.spotify_url ?? "",
        duration: r.duration ?? "",
        favorite: r.favorite ?? undefined,
        outlet: r.outlet ?? "",
        date: r.date ?? "",
      })) as OutletEpisode[],
    };
  },
});

function Portfolio() {
  const [filters, setFilters] = useState<ReadonlySet<Exclude<Filter, "all">>>(new Set());
  const { data } = useSuspenseQuery(portfolioQueryOptions);
  const { outlets, allArticles, allChroniques, allEpisodes } = data;

  const hasFav = filters.has("fav");
  const accessFilters = (["gratuit", "premium"] as const).filter((f) => filters.has(f));

  const scrollTop = () => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFilter = (value: Filter) => {
    setFilters((prev) => {
      if (value === "all") return new Set();
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
    scrollTop();
  };

  const newestHrefs = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Set(
      allArticles
        .filter((a) => {
          const t = new Date(a.date).getTime();
          return Number.isFinite(t) && t >= cutoff;
        })
        .map((a) => a.href),
    );
  }, [allArticles]);

  const applyFilter = (list: Article[]) => {
    let out = list;
    if (hasFav) out = out.filter((a) => a.favorite);
    if (accessFilters.length > 0) {
      const labels = accessFilters.map((f) => (f === "gratuit" ? "Gratuit" : "Premium"));
      out = out.filter((a) => labels.includes(a.access));
    }
    return out;
  };

  const applyEpisodeFilter = (list: Episode[]) => {
    let out = list;
    if (hasFav) out = out.filter((ep) => ep.favorite);

    // Masquer les podcasts UNIQUEMENT si Premium est activé SEUL
    if (filters.has("premium") && !filters.has("gratuit")) {
      return [];
    }

    return out;
  };

  // Scroll vers un article ciblé depuis la palette de commande (hash de l'URL).
  useEffect(() => {
    if (allArticles.length === 0) return;
    const raw = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
    if (!raw) return;
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(raw);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (tries++ < 20) setTimeout(tick, 100);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [allArticles]);

  // Tri automatique des médias par la date la plus récente de leur contenu publié.
  // Un média sans contenu publié est retiré.
  const sortedOutlets = useMemo(() => {
    const maxDate = (o: MediaOutlet): string => {
      const types = o.content_type ?? [];
      const dates: string[] = [
        ...(types.includes("articles") ? allArticles.filter((a) => a.outlet === o.slug).map((a) => a.date) : []),
        ...(types.includes("instagram") ? allChroniques.filter((c) => c.outlet === o.slug).map((c) => c.date) : []),
        ...(types.includes("podcast") ? allEpisodes.filter((e) => e.outlet === o.slug).map((e) => e.date) : []),
      ].filter(Boolean);
      if (dates.length === 0) return "";
      return dates.reduce((a, b) => (a > b ? a : b));
    };
    return outlets
      .map((o) => ({ o, d: maxDate(o) }))
      .filter((x) => x.d !== "")
      .sort((a, b) => (a.d < b.d ? 1 : a.d > b.d ? -1 : 0))
      .map((x) => x.o);
  }, [outlets, allArticles, allChroniques, allEpisodes]);

  const isActive = (value: Filter) =>
    value === "all" ? filters.size === 0 : filters.has(value as Exclude<Filter, "all">);

  const filterBtn = (value: Filter, label: React.ReactNode, active: string) => (
    <button
      type="button"
      aria-pressed={isActive(value)}
      onClick={() => toggleFilter(value)}
      className={`micro-interactive active:scale-95 flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.18em] transition ${
        isActive(value) ? active : "border-border text-foreground/70 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section className="portfolio-content mx-auto max-w-6xl px-6 pt-16 pb-24">
      {/* ------- COVER ------- */}
      <div className="relative overflow-hidden rounded-3xl border border-border bg-background">
        <div className="absolute inset-x-0 top-0 h-px bg-ocean" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-px bg-ocean" aria-hidden />
        <div className="grid gap-10 px-8 py-16 sm:px-12 sm:py-20 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.28em] text-ocean">Portfolio</p>
            <h1 className="font-display text-5xl leading-[1.02] sm:text-6xl" style={{ marginTop: "3rem" }}>
              Mes <em className="text-ocean">réalisations</em>
            </h1>
            <p className="mt-16 max-w-lg text-base leading-relaxed text-muted-foreground">
              Des podcasts au datajournalisme… quelques réalisations&nbsp;!
            </p>
          </div>
          <div className="mt-8 flex flex-col justify-center lg:col-span-2 lg:mt-0">
            <p className="text-[0.6rem] uppercase tracking-[0.3em] text-muted-foreground">Ils m’ont accueilli</p>
            <ul className="mt-6 flex flex-col gap-y-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-3">
              {[
                { alt: "Le Parisien", href: "#section-parisien" },
                { alt: "Sciences Po TV", href: "#section-sciences-po-tv" },
                { alt: "Citoyens.com", href: "#section-citoyens" },
                { alt: "Radio Germaine", href: "#section-radio-germaine" },
                { alt: "France Télévisions", href: "/parcours#journalisme" },
              ].map((l, i, arr) => (
                <li key={l.alt} className="flex items-center gap-x-4">
                  <span aria-hidden className="inline text-ocean-500 sm:hidden">
                    •
                  </span>
                  {l.href.startsWith("/") ? (
                    <Link
                      to="/parcours"
                      hash="journalisme"
                      aria-label={`Aller à la section ${l.alt}`}
                      className="micro-interactive active:scale-[0.98] group inline-flex items-baseline font-serif text-lg leading-none transition hover:text-ocean sm:text-xl"
                    >
                      {l.alt}
                    </Link>
                  ) : (
                    <a
                      href={l.href}
                      aria-label={`Aller à la section ${l.alt}`}
                      className="micro-interactive active:scale-[0.98] group inline-flex items-baseline font-serif text-lg leading-none transition hover:text-ocean sm:text-xl"
                    >
                      {l.alt}
                    </a>
                  )}
                  {i < arr.length - 1 && (
                    <span aria-hidden className="hidden text-muted-foreground/60 sm:inline">
                      —
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ------- FILTRES ------- */}
      <div className="relative sm:sticky top-0 z-30 mt-10 -mx-6 flex flex-wrap items-center gap-3 border-y border-border bg-background/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <span className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">Filtre</span>
        {filterBtn("all", "Tout", "border-foreground bg-foreground text-background")}
        {filterBtn(
          "fav",
          <>
            <span className={hasFav ? "text-white" : "text-red-600"}>♥</span>
            Coups de cœur
          </>,
          "border-red-600 bg-red-600 text-white",
        )}
        {filterBtn("gratuit", "Gratuit", "border-foreground bg-foreground text-background")}
        {filterBtn("premium", "Premium", "border-foreground bg-foreground text-background")}
      </div>

      {/* ------- MÉDIAS (boucle générique depuis media_outlets, triée par date la plus récente) ------- */}
      {sortedOutlets.map((o, idx) => {
        const wrapperClass = `${idx === 0 ? "mt-20" : "mt-24"} scroll-mt-24`;
        const footer = OUTLET_FOOTER[o.slug];

        const types = o.content_type ?? [];
        const blocks: React.ReactNode[] = [];

        // ---- ARTICLES ----
        if (types.includes("articles")) {
          const list = allArticles.filter((a) => a.outlet === o.slug);
          const listSorted = applyFilter(sortDesc(list));
          const hasSections = list.some((a) => a.section);
          const inSection = (name: "Articles" | "Dossiers") =>
            applyFilter(sortDesc(list.filter((a) => a.section === name)));
          blocks.push(
            <div key="articles">
              {hasSections ? (
                <>
                  <div className="mt-10">
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground">
                      Articles
                    </p>
                    {inSection("Articles").length === 0 ? (
                      <p className="mt-4 text-sm italic text-muted-foreground">
                        Aucun article ne correspond à ce filtre.
                      </p>
                    ) : (
                      <ul className="mt-4 grid gap-px sm:grid-cols-2 lg:grid-cols-3">
                        {inSection("Articles").map((a) => (
                          <RevealLi key={a.href} id={articleAnchorId(a.href)} className="scroll-mt-24">
                            <ArticleCard a={a} isNew={newestHrefs.has(a.href)} />
                          </RevealLi>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mt-12">
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground">
                      Dossiers co-écrits
                    </p>
                    {inSection("Dossiers").length === 0 ? (
                      <p className="mt-4 text-sm italic text-muted-foreground">
                        Aucun dossier ne correspond à ce filtre.
                      </p>
                    ) : (
                      <ul className="mt-4 grid gap-px sm:grid-cols-2">
                        {inSection("Dossiers").map((a) => (
                          <RevealLi key={a.href} id={articleAnchorId(a.href)} className="scroll-mt-24">
                            <ArticleCard a={a} isNew={newestHrefs.has(a.href)} />
                          </RevealLi>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-10">
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground">Articles</p>
                  {listSorted.length === 0 ? (
                    <p className="mt-4 text-sm italic text-muted-foreground">
                      Aucun article ne correspond à ce filtre.
                    </p>
                  ) : (
                    <ul className="mt-4 grid gap-px sm:grid-cols-2 lg:grid-cols-3">
                      {listSorted.map((a) => (
                        <RevealLi key={a.href} id={articleAnchorId(a.href)} className="scroll-mt-24">
                          <ArticleCard a={a} isNew={newestHrefs.has(a.href)} />
                        </RevealLi>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>,
          );
        }

        // ---- INSTAGRAM ----
        if (types.includes("instagram") && !hasFav) {
          // Masquer les chroniques UNIQUEMENT si Premium est activé SEUL
          if (filters.has("premium") && !filters.has("gratuit")) {
            return null;
          }

          const list = allChroniques.filter((c) => c.outlet === o.slug);
          if (list.length > 0) {
            blocks.push(
              <div key="instagram" className="mt-10 grid gap-10 lg:grid-cols-2">
                {list.map((v) => (
                  <Reveal key={v.reelUrl}>
                    <article className="flex flex-col gap-4" onPointerDown={() => recordContentView("instagram", v.id)}>
                      <div className="flex justify-center">
                        <InstagramEmbed url={v.reelUrl} title={v.title} />
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>,
            );
          }
        }

        // ---- PODCAST ----
        if (types.includes("podcast")) {
          const list = applyEpisodeFilter(allEpisodes.filter((e) => e.outlet === o.slug));
          if (list.length > 0) {
            blocks.push(
              <div key="podcast" className="mt-10 overflow-hidden border border-border">
                <div className="flex items-center justify-between border-b border-border bg-secondary/40 px-6 py-4">
                  <div>
                    <p className="font-serif text-xl">Playlist</p>
                    <p className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                      {list.length} épisodes · Saison 3
                    </p>
                  </div>
                  <span className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                    Saison complète
                  </span>
                </div>
                <ol>
                  {list.map((ep, i) => (
                    <RevealLi
                      key={ep.n}
                      className={`grid grid-cols-[auto_1fr_auto] items-center gap-4 px-6 py-4 transition-colors hover:bg-secondary/50 sm:gap-6 ${
                        i !== list.length - 1 ? "border-b border-border" : ""
                      }`}
                    >
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="micro-interactive active:scale-90 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-foreground transition-colors hover:bg-foreground hover:text-background"
                            aria-label={`Écouter ${ep.title}`}
                            onClick={() => recordContentView("podcast", ep.id)}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-56 p-2">
                          <div className="flex flex-col">
                            {ep.apple && (
                              <a
                                href={ep.apple}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="micro-interactive active:scale-[0.98] rounded-sm px-3 py-2 text-sm transition-colors hover:bg-secondary"
                              >
                                Écouter sur Apple Podcasts
                              </a>
                            )}
                            {ep.spotify && (
                              <a
                                href={ep.spotify}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="micro-interactive active:scale-[0.98] rounded-sm px-3 py-2 text-sm transition-colors hover:bg-secondary"
                              >
                                Écouter sur Spotify
                              </a>
                            )}
                            {!ep.apple && !ep.spotify && (
                              <p className="px-3 py-2 text-sm text-muted-foreground">
                                Lien d’écoute bientôt disponible.
                              </p>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <div className="min-w-0">
                        <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                          {ep.n}
                        </p>
                        <h3 className="mt-1 flex items-center gap-2 font-serif text-lg leading-snug">
                          <span>{ep.title}</span>
                          {ep.favorite && (
                            <span
                              title="Coup de cœur"
                              aria-label="Coup de cœur"
                              className="text-base leading-none text-red-600"
                            >
                              ♥
                            </span>
                          )}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{ep.excerpt}</p>
                      </div>
                      <div className="hidden shrink-0 flex-col items-end gap-1 text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground sm:flex">
                        <span>{ep.duration}</span>
                      </div>
                    </RevealLi>
                  ))}
                </ol>
              </div>,
            );
          }
        }

        if (blocks.length === 0) return null;
        return (
          <MediaSection
            key={o.slug}
            id={`section-${o.slug}`}
            className={wrapperClass}
            accentIndex={idx}
            eyebrow={o.eyebrow}
            title={o.title}
            place={o.place ?? undefined}
            period={o.period}
          >
            {blocks}
            {footer && (
              <p className="mt-6 text-xs text-muted-foreground">
                {footer.prefix}{" "}
                <a
                  href={footer.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-b border-foreground hover:opacity-70"
                >
                  {footer.label}
                </a>
              </p>
            )}
          </MediaSection>
        );
      })}
    </section>
  );
}
